import { fetchWithTimeout, sleep } from '../utils';

const WINDCAVE_TEST_URL = 'https://uat.windcave.com/hit/pos.aspx';
const WINDCAVE_PROD_URL = 'https://sec.windcave.com/hit/pos.aspx';
const WINDCAVE_DEVICE_ID = 'Device 1';
const WINDCAVE_VENDOR_ID = 'BiyoPOS';
const WINDCAVE_STATUS_POLL_INTERVAL_MS = 5000;
const WINDCAVE_MAX_STATUS_POLLS = 60;

function escapeXml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function getWindcaveFailureMessage(xmlText) {
  if (!xmlText) return 'Windcave payment was not approved.';

  try {
    const xmlDoc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const parserError = xmlDoc.getElementsByTagName('parsererror')[0];
    if (parserError) return null;

    const scr = xmlDoc.getElementsByTagName('Scr')[0];
    if (!scr) return null;

    const success = scr.getAttribute('success') || scr.getAttribute('Success');
    if (success && !['1', 'true', 'yes'].includes(success.toLowerCase())) {
      return scr.getAttribute('responseText') ||
        scr.getAttribute('ResponseText') ||
        scr.getAttribute('message') ||
        'Windcave payment was not approved.';
    }
  } catch (error) {
    return null;
  }

  return null;
}

function getXmlText(xmlDoc, tagName) {
  const element = xmlDoc.getElementsByTagName(tagName)[0];
  return element && element.textContent ? element.textContent.trim() : '';
}

function parseWindcaveStatus(xmlText) {
  const xmlDoc = new DOMParser().parseFromString(xmlText || '', 'application/xml');
  const parserError = xmlDoc.getElementsByTagName('parsererror')[0];
  if (parserError) {
    throw new Error('Unable to read Windcave terminal status.');
  }

  const complete = getXmlText(xmlDoc, 'Complete') === '1';
  const dl1 = getXmlText(xmlDoc, 'DL1').toUpperCase();
  const responseCode = getXmlText(xmlDoc, 'RC');
  const responseText = getXmlText(xmlDoc, 'RT').toUpperCase();
  const approved = complete && (dl1 === 'APPROVED' || responseText === 'APPROVED' || responseCode === '00');

  return {
    complete,
    approved,
    declined: complete && !approved,
    message: getXmlText(xmlDoc, 'RT') || getXmlText(xmlDoc, 'DL1') || 'Windcave payment was not approved.'
  };
}

async function postWindcaveXml(endpoint, body) {
  const response = await fetchWithTimeout(300000, fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/xml'
    },
    body
  }));

  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(`Windcave payment failed with status ${response.status}.`);
  }

  return responseText;
}

export const parseWindcaveConfig = (urlField) => {
  if (!urlField || typeof urlField !== 'string') {
    throw new Error('The Windcave payment type is not configured on the Dashboard.');
  }

  const [environment, stationId, user, authKey] = urlField.split(',').map(item => item.trim());
  if (!stationId || !user || !authKey) {
    throw new Error('Windcave payment type must be configured as "test|production, stationID, user, authKey".');
  }

  return {
    endpoint: environment.toLowerCase() === 'test' ? WINDCAVE_TEST_URL : WINDCAVE_PROD_URL,
    stationId,
    user,
    authKey
  };
};

export const buildWindcavePaymentXml = ({
  user,
  authKey,
  amount,
  stationId,
  txnRef,
  terminalName,
  terminalVersion
}) => {
  return `<Scr action="doScrHIT" user="${escapeXml(user)}" key="${escapeXml(authKey)}">
<Amount>${escapeXml(Number(amount).toFixed(2))}</Amount>
<Cur>USD</Cur>
<TxnType>Purchase</TxnType>
<Station>${escapeXml(stationId)}</Station>
<TxnRef>${escapeXml(txnRef)}</TxnRef>
<DeviceId>${escapeXml(WINDCAVE_DEVICE_ID)}</DeviceId>
<PosName>${escapeXml(terminalName)}</PosName>
<PosVersion>${escapeXml(terminalVersion)}</PosVersion>
<VendorId>${escapeXml(WINDCAVE_VENDOR_ID)}</VendorId>
<MRef>${escapeXml(txnRef)}</MRef>
</Scr>`;
};

export const buildWindcaveStatusXml = ({
  user,
  authKey,
  stationId,
  txnRef
}) => {
  return `<Scr action="doScrHIT" user="${escapeXml(user)}" key="${escapeXml(authKey)}">
<Station>${escapeXml(stationId)}</Station>
<TxnType>Status</TxnType>
<TxnRef>${escapeXml(txnRef)}</TxnRef>
</Scr>`;
};

export const makeWindcavePayment = async (urlField, amount, txnRef, terminalName, terminalVersion) => {
  const { endpoint, stationId, user, authKey } = parseWindcaveConfig(urlField);
  const body = buildWindcavePaymentXml({
    user,
    authKey,
    amount,
    stationId,
    txnRef,
    terminalName,
    terminalVersion
  });

  const initialResponseText = await postWindcaveXml(endpoint, body);
  const failureMessage = getWindcaveFailureMessage(initialResponseText);
  if (failureMessage) {
    throw new Error(failureMessage);
  }

  const statusBody = buildWindcaveStatusXml({
    user,
    authKey,
    stationId,
    txnRef
  });

  for (let pollCount = 0; pollCount < WINDCAVE_MAX_STATUS_POLLS; pollCount += 1) {
    await sleep(WINDCAVE_STATUS_POLL_INTERVAL_MS);

    const statusResponseText = await postWindcaveXml(endpoint, statusBody);
    const status = parseWindcaveStatus(statusResponseText);

    if (status.approved) {
      return statusResponseText;
    }

    if (status.declined) {
      throw new Error(status.message);
    }
  }

  throw new Error('Windcave payment timed out waiting for terminal approval.');
};
